import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Create separate admin client
const adminClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add admin token to requests
adminClient.interceptors.request.use((config) => {
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
  updatePassword: (data) => apiClient.post('/auth/update-password', data),
  updateEmail: (data) => apiClient.post('/auth/update-email', data),
  setup2FA: () => apiClient.post('/auth/2fa/setup'),
  enable2FA: (data) => apiClient.post('/auth/2fa/enable', data),
  disable2FA: (data) => apiClient.post('/auth/2fa/disable', data),
  verify2FA: (data) => apiClient.post('/auth/2fa/verify', { code: data.code }, { params: { email: data.email } }),
};

export const balanceAPI = {
  getBalance: () => apiClient.get('/balance'),
  getTransactions: () => apiClient.get('/balance/transactions'),
  createTopup: (data) => apiClient.post('/balance/topup', data),
  verifyTopup: (data) => apiClient.post('/balance/verify-topup', data),
  cancelTopup: (txId) => apiClient.post(`/balance/cancel-topup/${txId}`),
};

export const orderAPI = {
  createOrder: (data) => apiClient.post('/orders', data),
  verifyPayment: (data) => apiClient.post('/orders/verify-payment', data),
  getMyOrders: () => apiClient.get('/orders/my-orders'),
  updatePaymentMethod: (orderId, data) => apiClient.post(`/orders/${orderId}/update-payment-method`, data),
  validateVoucher: (data) => apiClient.post('/vouchers/validate', data),
};

export const priceAPI = {
  getSolPrice: () => apiClient.get('/price/sol'),
  getReactions: () => apiClient.get('/reactions'),
  getUserPricing: () => apiClient.get('/user/pricing'),
};

export const voucherAPI = {
  applyVoucher: (data) => apiClient.post('/vouchers/apply', data),
};

// Guest order API (no authentication required)
export const guestAPI = {
  getReactions: () => axios.get(`${API}/guest/reactions`),
  createOrder: (data) => axios.post(`${API}/guest/order`, data),
  getOrderStatus: (orderId) => axios.get(`${API}/guest/order/${orderId}/status`),
  updateOrderEmail: (orderId, email) => axios.post(`${API}/guest/order/${orderId}/email`, { email }),
  validateVoucher: (data) => axios.post(`${API}/guest/validate-voucher`, data),
  getPaymentQR: (paymentId) => `${API}/payments/nowpayments/${paymentId}/qr`,
};

export const bannerAPI = {
  getEnabledBanners: () => axios.get(`${API}/banners`),
  trackClick: (bannerId) => axios.post(`${API}/banners/${bannerId}/click`),
};

export const supportAPI = {
  createTicket: (data) => apiClient.post('/support/tickets', data),
  getMyTickets: () => apiClient.get('/support/my-tickets'),
  getTicketMessages: (ticketId) => apiClient.get(`/support/tickets/${ticketId}/messages`),
  sendMessage: (ticketId, data) => apiClient.post(`/support/tickets/${ticketId}/messages`, data),
};

export const adminAPI = {
  login: (data) => adminClient.post('/admin/login', data),
  getStats: () => adminClient.get('/admin/stats'),
  getUsers: () => adminClient.get('/admin/users'),
  getUserDetail: (userId) => adminClient.get(`/admin/users/${userId}`),
  blockUser: (userId) => adminClient.post(`/admin/users/${userId}/block`),
  deleteUserApiKey: (userId, keyId) => adminClient.delete(`/admin/users/${userId}/api-keys/${keyId}`),
  toggleUserApiKey: (userId, keyId) => adminClient.post(`/admin/users/${userId}/api-keys/${keyId}/toggle`),
  getUserCustomPrices: (userId) => adminClient.get(`/admin/users/${userId}/custom-prices`),
  setUserCustomPrice: (userId, data) => adminClient.post(`/admin/users/${userId}/custom-prices`, data),
  deleteUserCustomPrice: (userId, reactionType) => adminClient.delete(`/admin/users/${userId}/custom-prices/${reactionType}`),
  getOrders: () => adminClient.get('/admin/orders'),
  expireOrder: (orderId) => adminClient.post(`/admin/orders/${orderId}/expire`),
  getExpiredOrders: () => adminClient.get('/admin/orders/expired'),
  getMonthlyReport: (year, month) => adminClient.get(`/admin/reports/monthly?year=${year}&month=${month}`),
  getTickets: () => adminClient.get('/admin/tickets'),
  getTicketMessages: (ticketId) => adminClient.get(`/admin/tickets/${ticketId}/messages`),
  sendMessage: (ticketId, data) => adminClient.post(`/admin/tickets/${ticketId}/messages`, data),
  updateTicketStatus: (ticketId, data) => adminClient.patch(`/admin/tickets/${ticketId}/status`, data),
  closeTicket: (ticketId) => adminClient.post(`/admin/tickets/${ticketId}/close`),
  acceptPayment: (orderId) => adminClient.post(`/admin/orders/${orderId}/accept-payment`),
  rejectPayment: (orderId, data) => adminClient.post(`/admin/orders/${orderId}/reject-payment`, data),
  completeOrder: (orderId) => adminClient.post(`/admin/orders/${orderId}/complete`),
  processOrderAuto: (orderId, vendor) => adminClient.post(`/admin/orders/${orderId}/process-auto`, { vendor }),
  processOrderManual: (orderId) => adminClient.post(`/admin/orders/${orderId}/process-manual`),
  updateCredentials: (data) => adminClient.post('/admin/update-credentials', data),
  getVouchers: () => adminClient.get('/admin/vouchers'),
  createVoucher: (data) => adminClient.post('/admin/vouchers', data),
  updateVoucher: (id, active) => adminClient.put(`/admin/vouchers/${id}?active=${active}`),
  deleteVoucher: (id) => adminClient.delete(`/admin/vouchers/${id}`),
  deleteAllData: () => adminClient.post('/admin/delete-all-data'),
  // Boostlegends settings
  getBoostlegendsSettings: () => adminClient.get('/admin/boostlegends-settings'),
  updateBoostlegendsSettings: (data) => adminClient.post('/admin/boostlegends-settings', data),
  testBoostlegendsConnection: () => adminClient.post('/admin/boostlegends-test'),
  // Product mappings
  getProductMappings: () => adminClient.get('/admin/product-mappings'),
  updateProductMapping: (data) => adminClient.post('/admin/product-mappings', data),
  // Dexmoji Vendor settings
  getDexmojiSettings: () => adminClient.get('/admin/dexmoji-settings'),
  updateDexmojiSettings: (data) => adminClient.post('/admin/dexmoji-settings', data),
  testDexmojiConnection: () => adminClient.post('/admin/dexmoji-test'),
  // Deposits
  getDeposits: () => adminClient.get('/admin/deposits'),
  approveDeposit: (txId) => adminClient.post(`/admin/deposits/${txId}/approve`),
  rejectDeposit: (txId, data) => adminClient.post(`/admin/deposits/${txId}/reject`, data),
  // Google OAuth settings
  getGoogleOAuthSettings: () => adminClient.get('/admin/google-oauth-settings'),
  updateGoogleOAuthSettings: (data) => adminClient.post('/admin/google-oauth-settings', data),
  // Gtag settings
  getGtagSettings: () => adminClient.get('/admin/gtag-settings'),
  updateGtagSettings: (data) => adminClient.post('/admin/gtag-settings', data),
  // Telegram Bot settings
  getTelegramBotSettings: () => adminClient.get('/admin/telegram-bot-settings'),
  updateTelegramBotSettings: (data) => adminClient.post('/admin/telegram-bot-settings', data),
  testTelegramBot: () => adminClient.post('/admin/telegram-bot-test'),
  // Fonnte WhatsApp settings
  getFonnteSettings: () => adminClient.get('/admin/fonnte-settings'),
  updateFonnteSettings: (data) => adminClient.post('/admin/fonnte-settings', data),
  testFonnteConnection: () => adminClient.post('/admin/fonnte-test'),
  // NOWPayments settings
  getNowpaymentsSettings: () => adminClient.get('/admin/nowpayments-settings'),
  updateNowpaymentsSettings: (data) => adminClient.post('/admin/nowpayments-settings', data),
  testNowpaymentsConnection: () => adminClient.post('/admin/nowpayments-test'),
  getOrderNowpaymentsStatus: (orderId) => adminClient.get(`/admin/orders/${orderId}/nowpayments-status`),
  syncNowpaymentsStatus: () => adminClient.post('/admin/sync-nowpayments-status'),
  expirePendingOrders: () => adminClient.post('/admin/expire-pending-orders'),
  // SMTP Settings
  getSmtpSettings: () => adminClient.get('/admin/smtp-settings'),
  updateSmtpSettings: (data) => adminClient.post('/admin/smtp-settings', data),
  testSmtpConnection: () => adminClient.post('/admin/smtp-test'),
  // Database Backup & Restore
  getBackupInfo: () => adminClient.get('/admin/backup-info'),
  downloadBackup: () => adminClient.get('/admin/backup', { responseType: 'blob' }),
  restoreBackup: (formData) => adminClient.post('/admin/restore', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Banner Management
  getBanners: () => adminClient.get('/admin/banners'),
  createBanner: (data) => adminClient.post('/admin/banners', data),
  updateBanner: (id, data) => adminClient.put(`/admin/banners/${id}`, data),
  deleteBanner: (id) => adminClient.delete(`/admin/banners/${id}`),
  toggleBanner: (id) => adminClient.patch(`/admin/banners/${id}/toggle`),
  uploadBannerImage: (formData) => adminClient.post('/admin/banners/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // NOWPayments Crypto
  createCryptoPayment: (data) => apiClient.post('/payments/nowpayments/create', data),
  getCryptoPaymentStatus: (paymentId) => apiClient.get(`/payments/nowpayments/${paymentId}/status`),
  getCryptoPaymentQR: (paymentId) => `${API}/payments/nowpayments/${paymentId}/qr`,
};

// Public API for Google OAuth
export const googleOAuthAPI = {
  getStatus: () => axios.get(`${API}/google-oauth-status`),
  authenticate: (credential) => axios.post(`${API}/auth/google`, { credential }),
};

// Public API for Gtag
export const gtagAPI = {
  getCode: () => axios.get(`${API}/gtag`),
};

// API Developer
export const apiDeveloperAPI = {
  getApiKeys: () => apiClient.get('/api-keys'),
  createApiKey: (data) => apiClient.post('/api-keys', data),
  updateApiKey: (keyId, data) => apiClient.put(`/api-keys/${keyId}`, data),
  deleteApiKey: (keyId) => apiClient.delete(`/api-keys/${keyId}`),
  regenerateApiKey: (keyId) => apiClient.post(`/api-keys/${keyId}/regenerate`),
  getApiDocs: () => axios.get(`${API}/v2/docs`),
};

export default apiClient;
